import { useEffect, useMemo, useState } from 'react'
import { SearchSelect, type ComboboxItem } from '@/components/ui'
import { useLookup } from '../api/onboarding-queries'

export interface LookupPickerProps {
  kind: 'uom' | 'categories'
  value: readonly [number, string] | null
  onChange: (next: readonly [number, string] | null) => void
  placeholder?: string
  disabled?: boolean
  ariaLabel?: string
}

/** Odoo unit-of-measure / product-category picker (server search). */
export function LookupPicker({ kind, value, onChange, placeholder, disabled, ariaLabel }: LookupPickerProps) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])
  const { data, isFetching } = useLookup(kind, debounced)
  const items: ComboboxItem[] = useMemo(
    () =>
      (data?.items ?? []).map((r) => ({
        value: String(r.id),
        label: r.name,
        meta: r.category ?? undefined,
      })),
    [data],
  )
  return (
    <div aria-label={ariaLabel}>
      <SearchSelect
        items={items}
        value={value ? String(value[0]) : undefined}
        selectedLabel={value ? value[1] : null}
        onSelect={(v) => {
          const row = data?.items.find((r) => String(r.id) === v)
          if (row) onChange([row.id, row.name])
        }}
        onClear={() => onChange(null)}
        onQueryChange={setQuery}
        placeholder={placeholder ?? (kind === 'uom' ? 'Search units…' : 'Search categories…')}
        emptyMessage={isFetching ? 'Searching…' : 'No matches'}
        disabled={disabled}
      />
    </div>
  )
}
